/**
 * Version check utility to ensure users always have the latest version
 * Uses localStorage to track the current version
 */

const VERSION_KEY = 'app-version';

/**
 * Get the current app version from the login scene
 */
export function getCurrentVersion(): string {
    // This will be set by the login scene when it loads
    return (window as any).__APP_VERSION__ || 'unknown';
}

/**
 * Set the current app version
 */
export function setCurrentVersion(version: string): void {
    (window as any).__APP_VERSION__ = version;
    localStorage.setItem(VERSION_KEY, version);
}

/**
 * Check if a new version is available
 * Returns 'first-run', 'updated', or 'same'
 */
export function checkForNewVersion(): 'first-run' | 'updated' | 'same' {
    try {
        const currentVersion = getCurrentVersion();
        const storedVersion = localStorage.getItem(VERSION_KEY);

        // First run - no stored version
        if (!storedVersion) {
            setCurrentVersion(currentVersion);
            return 'first-run';
        }

        // Version changed - update detected
        if (storedVersion !== currentVersion) {
            setCurrentVersion(currentVersion);
            return 'updated';
        }

        // Same version
        return 'same';
    } catch (error) {
        console.error('Error checking version:', error);
        return 'same';
    }
}

/**
 * Force reload the app to get the latest version
 */
export function forceReload(): void {
    // Clear all caches before reload
    if ('caches' in window) {
        caches.keys().then(names => {
            names.forEach(name => {
                caches.delete(name);
            });
        }).finally(() => {
            (window as Window).location.reload();
        });
    } else {
        (window as Window).location.reload();
    }
}

/**
 * Initialize version checking - should be called on app startup
 * Checks version once on page load only
 * Automatically reloads if a new version is detected (but not on first run)
 */
export function initVersionCheck(): void {
    const versionStatus = checkForNewVersion();

    if (versionStatus === 'updated') {
        console.info('New version detected, reloading to apply changes...');
        // Force reload to get the new version
        forceReload();
    } else if (versionStatus === 'first-run') {
        console.info('Welcome! Running version:', getCurrentVersion());
    } else {
        console.info('Running current version:', getCurrentVersion());
    }
}
